import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'framer-motion';
import React from 'react';

import { Spinner } from './Animations';
import { cn } from './cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-button font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-gray-800 text-white hover:bg-gray-900',
        primary: 'bg-primary-500 text-text-on-accent hover:bg-primary-600',
        danger: 'bg-status-danger-emphasis text-text-on-danger hover:bg-danger-600',
        outline: 'border border-border bg-surface hover:bg-surface-hover text-text-primary',
        ghost: 'hover:bg-surface-hover text-text-primary',
        link: 'text-text-link underline-offset-4 hover:underline p-0 h-auto',
        stealth: 'text-text-secondary hover:bg-surface-hover',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-11 px-5',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /**
   * Keep the button in the tab order while it is disabled. It is marked `aria-disabled` instead of
   * `disabled`, looks and swallows clicks the same, and can still take focus — so a keyboard or
   * screen-reader user can reach it and learn why it is unavailable, e.g. from a wrapping `Tooltip`,
   * which opens on focus. A native `disabled` button cannot be focused, which left that explanation
   * to a mouse pointer alone. Opt-in: without it, `disabled` behaves exactly as before.
   */
  focusableWhenDisabled?: boolean;
}

const MotionButton = motion.create('button');

const preventActivation = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault();

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading, children, disabled, focusableWhenDisabled, ...props },
    ref
  ) => {
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...(disabled || loading ? { 'aria-disabled': true } : {})}
          {...props}
        >
          {children}
        </Slot>
      );
    }
    const inactive = disabled || loading;
    // The same look and pointer behaviour `disabled:` gives a native disabled button, keyed on the
    // flag rather than the pseudo-class, which this button never enters.
    const focusableInactive = focusableWhenDisabled && inactive;
    return (
      <MotionButton
        type="button"
        className={cn(
          buttonVariants({ variant, size, className }),
          focusableInactive && 'pointer-events-none opacity-50'
        )}
        ref={ref}
        disabled={focusableInactive ? undefined : inactive}
        aria-disabled={focusableInactive ? true : undefined}
        whileHover={!inactive ? { scale: 1.02 } : undefined}
        whileTap={!inactive ? { scale: 0.97 } : undefined}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        {...(props as any)}
        // After the spread: Enter and Space still reach a focusable button, and must do nothing.
        {...(focusableInactive ? { onClick: preventActivation } : {})}
      >
        {loading && (
          <Spinner className="mr-1 h-4 w-4" viewBox="0 0 24 24" fill="none" transition={{ duration: 1 }}>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </Spinner>
        )}
        {children}
      </MotionButton>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
